// src/screens/Marketplace/DepartmentResults.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../api';
import { keyToLabel } from '../../constants/departments';

import placeholderBanner from '../../assets/placeholder-banner.png';

// Dept-specific banners
import artBanner from '../../assets/Art-banner.png';
import booksBanner from '../../assets/Books-banner.png';
import clothingBanner from '../../assets/Clothing-banner.png';
import coursesBanner from '../../assets/Courses-banner.png';
import electronicsBanner from '../../assets/Electronics-banner.png';
import gamingBanner from '../../assets/Gaming-banner.png';
import healthBanner from '../../assets/Health-banner.png';
import homeBanner from '../../assets/Home-banner.png';
import jewelryBanner from '../../assets/Jewelry-banner.png';
import luggageBanner from '../../assets/Luggage-banner.png';
import nftBanner from '../../assets/nft-banner.png';
import sportsBanner from '../../assets/Sports-banner.png';
import subscriptionsBanner from '../../assets/Subscriptions-banner.png';
import toysBanner from '../../assets/Toys-banner.png';

import './DepartmentResults.css';

const PAGE_SIZE = 12;

// ---- DEBUG helpers ---------------------------------------------------------
const isDebug = () => !!localStorage.getItem('DEBUG_DEPT');
const dbg = (...args) => { if (isDebug()) console.log('[DeptResults]', ...args); };
const warn = (...args) => { if (isDebug()) console.warn('[DeptResults]', ...args); };

// ---- Dept normalize/tolerant compare --------------------------------------
const norm = (s) => (s ?? 'Other').toString().trim().toLowerCase();
const matchesDept = (pDept, wanted) => {
  const a = norm(pDept);
  const b = norm(wanted);
  if (a === b) return true;
  // simple singular/plural tolerance (Essential vs Essentials)
  if (a === `${b}s`) return true;
  if (`${a}s` === b) return true;
  return false;
};

// ---- Dept → Banner helper --------------------------------------------------
function getDeptBanner(key, label) {
  const k = norm(key);
  const l = norm(label);
  const s = `${k} ${l}`;

  if (s.includes('nft')) return nftBanner;

  if (s.includes('art')) return artBanner;
  if (s.includes('book')) return booksBanner;
  if (s.includes('clothing') || s.includes('fashion')) return clothingBanner;
  if (s.includes('course') || s.includes('education')) return coursesBanner;
  if (s.includes('electronic') || s.includes('gadget')) return electronicsBanner;
  if (s.includes('gaming') || s.includes('game')) return gamingBanner;
  if (s.includes('health') || s.includes('beauty')) return healthBanner;
  if (s.includes('home') || s.includes('living')) return homeBanner;
  if (s.includes('jewelry') || s.includes('jewellery') || s.includes('accessor')) return jewelryBanner;
  if (s.includes('luggage') || s.includes('travel')) return luggageBanner;
  if (s.includes('sport')) return sportsBanner;
  if (s.includes('subscription') || s.includes('service')) return subscriptionsBanner;
  if (s.includes('toy') || s.includes('kids') || s.includes('children')) return toysBanner;

  return placeholderBanner;
}

// ----------------------------------------------------------------------------
/**
 * Derives the effective price in Pi and whether a sale is active.
 * Supports server-enriched fields and falls back to heuristics.
 */
function getEffectivePricePi(p, piToUsdRate) {
  // 0) Prefer server-enriched fields if available
  if (typeof p?.effectivePricePi === 'number') {
    const isOnSale = !!(p?.sale?.active || p?.isOnSale === true);
    const percentOff = typeof p?.sale?.percentOff === 'number' ? p.sale.percentOff : null;
    if (isDebug()) dbg('Using server-enriched price', {
      id: p?._id, effectivePricePi: p.effectivePricePi, isOnSale, percentOff
    });
    return {
      pricePi: p.effectivePricePi,
      isOnSale,
      percentOff,
    };
  }

  // 1) Compute from raw fields if not enriched
  const isDynamic = p?.pricingType === 'dynamic' && typeof p?.usdPrice === 'number';
  const basePi = (isDynamic && typeof piToUsdRate === 'number' && piToUsdRate > 0)
    ? (p.usdPrice / piToUsdRate)
    : (typeof p?.price === 'number' ? p.price : null);

  // helper: normalize possible sale fields
  const sale = p?.sale || p?.flashSale || p?.activeSale || {};
  const saleActive =
    !!sale?.active ||
    typeof p?.activeSalePricePi === 'number' ||
    typeof p?.activeSalePriceUsd === 'number' ||
    typeof p?.salePrice === 'number' ||
    typeof p?.salePricePi === 'number' ||
    typeof p?.salePriceUsd === 'number';

  // compute sale price in Pi if present
  let salePi = null;

  // Priority: explicit Pi price
  if (typeof sale?.pricePi === 'number') salePi = sale.pricePi;
  else if (typeof p?.activeSalePricePi === 'number') salePi = p.activeSalePricePi;
  else if (typeof p?.salePricePi === 'number') salePi = p.salePricePi;

  // Next: explicit USD price -> convert to Pi
  const usdToPi = (usd) =>
    (typeof usd === 'number' && typeof piToUsdRate === 'number' && piToUsdRate > 0)
      ? (usd / piToUsdRate)
      : null;

  if (salePi == null) {
    if (typeof sale?.priceUsd === 'number') salePi = usdToPi(sale.priceUsd);
    else if (typeof p?.activeSalePriceUsd === 'number') salePi = usdToPi(p.activeSalePriceUsd);
    else if (typeof p?.salePriceUsd === 'number') salePi = usdToPi(p.salePriceUsd);
  }

  // Fallback: single generic salePrice (assume it's Pi)
  if (salePi == null && typeof p?.salePrice === 'number') salePi = p.salePrice;

  // If we still don't have base or sale, return base only
  if (basePi == null) {
    if (isDebug()) warn('No basePi; cannot compute price', { id: p?._id, name: p?.name, p });
    return { pricePi: null, isOnSale: false, percentOff: null };
  }

  if (!saleActive || salePi == null) {
    if (isDebug()) dbg('No active sale; using basePi', { id: p?._id, basePi, p });
    return { pricePi: basePi, isOnSale: false, percentOff: null };
  }

  // Guard against weird values
  const finalSalePi = Math.max(0, salePi);
  const percentOff = Math.max(
    0,
    Math.min(100, Math.round((1 - finalSalePi / basePi) * 100))
  );

  if (isDebug()) {
    dbg('SALE detected', {
      id: p?._id,
      name: p?.name,
      basePi,
      salePi: finalSalePi,
      percentOff
    });
  }

  return {
    pricePi: finalSalePi,
    isOnSale: true,
    percentOff: Number.isFinite(percentOff) ? percentOff : null,
  };
}

export default function DepartmentResults({ user }) {
  const { key } = useParams();
  const navigate = useNavigate();
  const label = useMemo(() => keyToLabel(key), [key]);

  const [title, setTitle] = useState(label);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [piToUsdRate, setPiToUsdRate] = useState(null);

  // NEW: hero banner load state
  const [heroLoaded, setHeroLoaded] = useState(false);

  // Pick banner based on current department
  const heroBanner = useMemo(
    () => getDeptBanner(key, label),
    [key, label]
  );

  // Reset heroLoaded when banner changes (switch departments)
  useEffect(() => {
    setHeroLoaded(false);
  }, [heroBanner]);

  // Infinite scroll refs
  const sentinelRef = useRef(null);
  const scrollHostRef = useRef(null);
  const observerRef = useRef(null);

  // Pi rate (cached)
  useEffect(() => {
    let timer;
    const fetchPiRate = async () => {
      try {
        const res = await api.get('/convert/rate');
        if (res.data?.piToUsd) {
          setPiToUsdRate(res.data.piToUsd);
          if (isDebug()) dbg('Pi rate updated', res.data.piToUsd);
        } else if (isDebug()) warn('No piToUsd in /convert/rate payload', res.data);
      } catch (e) {
        if (isDebug()) warn('Failed to fetch Pi rate', e?.message || e);
      }
    };
    fetchPiRate();
    timer = setInterval(fetchPiRate, 600000);
    return () => clearInterval(timer);
  }, []);

  // Load page 1 whenever department changes
  useEffect(() => {
    setItems([]);
    setPage(1);
    setHasMore(false);
    setInitialLoaded(false);

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const url = `/products?sort=newest&page=1&limit=${PAGE_SIZE}`;
        if (isDebug()) dbg('Fetching page 1', { url, label });
        const res = await api.get(url);
        const raw = res.data?.products || [];

        if (isDebug()) {
          dbg('Raw products count', raw.length);
          const uniqDepts = Array.from(new Set(raw.map(p => (p?.department || 'Other'))));
          dbg('Dept label resolved to:', label, 'Unique depts in payload:', uniqDepts);
          raw.slice(0, 3).forEach((p, idx) => {
            dbg(`Raw[${idx}]`, {
              id: p?._id, name: p?.name, department: p?.department,
              price: p?.price, pricingType: p?.pricingType, usdPrice: p?.usdPrice,
              sale: p?.sale, flashSale: p?.flashSale, activeSale: p?.activeSale,
              salePrice: p?.salePrice, salePricePi: p?.salePricePi, salePriceUsd: p?.salePriceUsd,
              activeSalePricePi: p?.activeSalePricePi, activeSalePriceUsd: p?.activeSaleUsd,
              effectivePricePi: p?.effectivePricePi,
              isOnSale: p?.isOnSale,
            });
          });
        }

        const filtered = raw.filter(p => matchesDept(p?.department, label));
        if (isDebug()) dbg('Filtered by department', label, '=>', filtered.length);

        setItems(filtered);
        setHasMore(res.data?.hasMore || filtered.length === PAGE_SIZE);
        setTitle(label);
        setInitialLoaded(true);
      } catch (err) {
        if (isDebug()) warn('Failed to load page 1', err?.message || err);
        if (!cancelled) setInitialLoaded(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [label]);

  const fetchNextPage = async () => {
    if (loading || !hasMore) return;
    const nextPage = page + 1;

    setLoading(true);
    try {
      const url = `/products?sort=newest&page=${nextPage}&limit=${PAGE_SIZE}`;
      if (isDebug()) dbg('Fetching next page', { url, label });
      const res = await api.get(url);
      const raw = res.data?.products || [];
      const filtered = raw.filter(p => matchesDept(p?.department, label));

      if (isDebug()) dbg('Next page filtered count', filtered.length);

      setItems(prev => [...prev, ...filtered]);
      setHasMore(res.data?.hasMore || filtered.length === PAGE_SIZE);
      setPage(nextPage);
    } catch (e) {
      if (isDebug()) warn('Failed to fetch next page', e?.message || e);
    }
    finally {
      setLoading(false);
    }
  };

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    if (!sentinelRef.current) return;

    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          if (isDebug()) dbg('Sentinel intersecting -> fetchNextPage()');
          fetchNextPage();
        }
      },
      {
        root: scrollHostRef.current || null,
        rootMargin: '120px',
        threshold: 0.01,
      }
    );

    observerRef.current.observe(sentinelRef.current);

    return () => {
      observerRef.current?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sentinelRef.current, scrollHostRef.current, page, hasMore, loading]);

  const sevenDaysAgo = useMemo(
    () => new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
    []
  );

  return (
    <div className="drx-page">
      {/* Hero goes INSIDE the scrolling content (like BannerResults) */}
      <div className="drx-scroll" ref={scrollHostRef}>
        <div className="drx-hero">
          {/* shimmer while hero loads */}
          {!heroLoaded && <div className="drx-hero-skeleton" />}

          <img
            className={`drx-hero-img ${heroLoaded ? 'drx-hero-img--loaded' : ''}`}
            src={heroBanner}
            alt={`${label} banner`}
            onLoad={() => setHeroLoaded(true)}
            onError={() => setHeroLoaded(true)} // even on error, hide shimmer
          />

          <div className="drx-hero-overlay" />
          <button
            className="drx-hero-back"
            onClick={() => navigate('/marketplace')}
            aria-label="Back to marketplace"
            title="Back"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
              <path
                d="M15 18l-6-6 6-6"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <div className="drx-hero-text">
            <h1 className="drx-hero-title">{title}</h1>
          </div>
        </div>

        <div className="drx-wrap">
          <div className="drx-grid">
            {!initialLoaded &&
              Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="drx-card drx-skeleton">
                  <div className="drx-img drx-sk" />
                  <div className="drx-name drx-sk-line drx-w-80" />
                  <div className="drx-price drx-sk-line drx-w-40" />
                </div>
              ))}

            {items.map((p, idx) => {
              const createdAt = p?.createdAt ? new Date(p.createdAt) : null;
              const showNewBadge = createdAt && createdAt >= sevenDaysAgo;

              const { pricePi, isOnSale, percentOff } = getEffectivePricePi(p, piToUsdRate);
              const priceLabel = pricePi != null ? `@ ${pricePi.toFixed(2)} Pi` : '@ —';
              const saleBadgeText = (typeof percentOff === 'number' && percentOff > 0)
                ? `-${percentOff}%`
                : '-SALE';

              if (isDebug() && idx < 5) {
                dbg('Render card', {
                  id: p?._id, name: p?.name,
                  isOnSale, percentOff,
                  priceLabel,
                  pricingType: p?.pricingType, usdPrice: p?.usdPrice, price: p?.price,
                  sale: p?.sale, flashSale: p?.flashSale, activeSale: p?.activeSale,
                  salePrice: p?.salePrice, salePricePi: p?.salePricePi, salePriceUsd: p?.salePriceUsd,
                  activeSalePricePi: p?.activeSalePricePi, activeSalePriceUsd: p?.activeSalePriceUsd,
                  effectivePricePi: p?.effectivePricePi,
                  isOnSaleFlag: p?.isOnSale,
                  piToUsdRate
                });
              }

              return (
                <button
                  key={p._id}
                  className="drx-card"
                  onClick={() => navigate(`/product/${p._id}`)}
                  aria-label={`View ${p.name}`}
                >
                  <div className="drx-imgwrap">
                    <img
                      className="drx-img"
                      src={p.media?.[0] || 'https://via.placeholder.com/600x600?text=No+Image'}
                      alt={p.name}
                      onError={(e) => {
                        e.currentTarget.src = 'https://via.placeholder.com/600x600?text=Image';
                      }}
                    />
                    {isOnSale && <span className="drx-chip drx-chip-sale">{saleBadgeText}</span>}
                    {showNewBadge && <span className="drx-chip drx-chip-new">New</span>}
                  </div>

                  <div className="drx-name" title={p.name}>{p.name}</div>

                  <div className="drx-meta">
                    <div className="drx-price">
                      {priceLabel}
                    </div>
                    {p.expectedDeliveryDays && <div className="drx-delivery">{p.expectedDeliveryDays}d</div>}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="drx-footer">
            {initialLoaded && items.length === 0 && (
              <div className="drx-empty">No products in this department yet.</div>
            )}
            {!hasMore && initialLoaded && items.length > 0 && (
              <div className="drx-endhint">You’ve reached the end</div>
            )}
            {loading && <div className="drx-loading">Loading…</div>}
          </div>

          {hasMore && <div ref={sentinelRef} className="drx-sentinel" aria-hidden />}
        </div>
      </div>
    </div>
  );
}
